import React, { useCallback, useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { startOfMonth, endOfMonth } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEv_list, fetchIv_list } from '../../../store';
import { fetchAndBuildMonthlyGeneration } from './services/monthlyGenerationService';
import ReportToolbar from './components/ReportToolbar';
import ChartComponent from './components/ChartComponent';
import TableComponent from './components/TableComponent';
import {
  MONTHLY_PERIOD_OPTIONS,
  MONTHLY_PERIOD_RANGES,
} from './utils/reportPeriodRanges';
import './GenHourly.css';
import './GenDaily.css';

const MAX_MONTHLY_RANGE_MONTHS = 12;

const GenMonth = () => {
  const dispatch = useDispatch();
  const ivList = useSelector((state) => state.Iv_list);
  const Ev_list = useSelector((state) => state.Ev_list || {});

  const [viewMode, setViewMode] = useState('total');
  const [displayType, setDisplayType] = useState('chart');
  const [tempViewMode, setTempViewMode] = useState('total');
  const [tempDisplayType, setTempDisplayType] = useState('chart');

  const [startMonth, setStartMonth] = useState(startOfMonth(new Date()));
  const [endMonth, setEndMonth] = useState(endOfMonth(new Date()));
  const [activePeriod, setActivePeriod] = useState('thisMonth');

  const [displayedData, setDisplayedData] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [lastSearchMode, setLastSearchMode] = useState('total');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchIv_list());
    dispatch(fetchEv_list());
  }, [dispatch]);

  const runSearch = useCallback(async (searchStart = startMonth, searchEnd = endMonth) => {
    try {
      setIsLoading(true);
      const { displayed, csv } = await fetchAndBuildMonthlyGeneration({
        startMonth: searchStart,
        endMonth: searchEnd,
        mode: tempViewMode,
        ivList,
        Ev_list,
      });

      setDisplayedData(displayed);
      setCsvData(csv);
      setLastSearchMode(tempViewMode);
      setViewMode(tempViewMode);
      setDisplayType(tempDisplayType);
    } catch (error) {
      console.error(error);
      alert(error.message || '데이터를 가져오는 중 오류가 발생했습니다.');
      setDisplayedData(null);
      setCsvData([]);
    } finally {
      setIsLoading(false);
    }
  }, [startMonth, endMonth, tempViewMode, tempDisplayType, ivList, Ev_list]);

  const handleSearch = useCallback(() => {
    runSearch(startMonth, endMonth);
  }, [runSearch, startMonth, endMonth]);

  const applyPeriod = useCallback(async (periodKey) => {
    const range = MONTHLY_PERIOD_RANGES[periodKey]();
    setStartMonth(range.start);
    setEndMonth(range.end);
    setActivePeriod(range.key);
    await runSearch(range.start, range.end);
  }, [runSearch]);

  return (
    <div className="gen-hourly-page gen-daily-page">
      <ReportToolbar
        periodOptions={MONTHLY_PERIOD_OPTIONS}
        activePeriod={activePeriod}
        onPeriodClick={applyPeriod}
        startDate={startMonth}
        endDate={endMonth}
        setStartDate={setStartMonth}
        setEndDate={setEndMonth}
        pickerMode="month"
        maxRangeMonths={MAX_MONTHLY_RANGE_MONTHS}
        rangeHint="조회 기간은 최대 12개월까지 선택할 수 있습니다."
        tempViewMode={tempViewMode}
        setTempViewMode={setTempViewMode}
        tempDisplayType={tempDisplayType}
        setTempDisplayType={setTempDisplayType}
        handleSearch={handleSearch}
        isLoading={isLoading}
        csvData={csvData}
        csvFilename="월간발전량.csv"
        onDateChange={() => setActivePeriod(null)}
      />

      {displayType === 'chart' ? (
        <ChartComponent displayedData={displayedData} lastSearchMode={lastSearchMode} />
      ) : (
        <TableComponent displayedData={displayedData} viewMode={viewMode} ivList={ivList} />
      )}
    </div>
  );
};

export default GenMonth;
